/**
 * Gateway error codes tied to the user's Google authorization.
 *
 * Under the `drive.file` scope the gateway can answer 401 because the stored
 * Google grant needs a new sign-in. That is not an expired BAO session: the
 * auth interceptor must not rotate the session for it, and the UI should ask
 * the user to sign out and back in.
 */

/** 401 codes that only a new Google sign-in fixes. */
export const GOOGLE_REAUTH_ERROR_CODES: ReadonlySet<string> = new Set([
    "GOOGLE_TOKENS_NOT_FOUND",
    "GOOGLE_NO_REFRESH_TOKEN",
    "GOOGLE_AUTH_REVOKED",
    "GOOGLE_SCOPE_INSUFFICIENT",
]);

/** Message shown for any {@link GOOGLE_REAUTH_ERROR_CODES} error. */
export const GOOGLE_REAUTH_MESSAGE =
    "Votre connexion Google doit être renouvelée. Déconnectez-vous puis reconnectez-vous.";

/**
 * Read the gateway `error_code` of a failed request.
 *
 * @param err - Any error, typically an axios error.
 * @returns The `error_code` of the response body, or undefined.
 */
export const getApiErrorCode = (err: unknown): string | undefined => {
    const code = (err as { response?: { data?: { error_code?: unknown } } } | null)?.response?.data?.error_code;
    return typeof code === "string" ? code : undefined;
};

/**
 * Tell whether a failed request needs a new Google sign-in.
 *
 * @param err - Any error, typically an axios error.
 * @returns True when its `error_code` is one of {@link GOOGLE_REAUTH_ERROR_CODES}.
 */
export const isGoogleReauthError = (err: unknown): boolean => {
    const code = getApiErrorCode(err);
    return code !== undefined && GOOGLE_REAUTH_ERROR_CODES.has(code);
};
